import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { fetchApi } from '@/lib/api';
import { Footer, SEO } from '@/components';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';
import {
  FiArrowLeft,
  FiDollarSign,
  FiMessageCircle,
  FiCalendar,
  FiFileText,
  FiSmartphone,
  FiBell,
  FiCheckCircle,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiTrendingDown,
  FiCopy,
  FiFolder,
} from 'react-icons/fi';
import { getPricingFeatures, type PricingFeature } from '@/lib/pricing-features';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import shipSvg from '@/assets/ship.svg';
import devicesPng from '@/assets/devices.png';
import laptopExamplePng from '@/assets/laptop-example.png';
import harborSs1Png from '@/assets/harbor_ss1.png';
import harborSsCalendarPng from '@/assets/harbor_ss_calendar.png';
import harborSsChatPng from '@/assets/harbor_ss_chat.png';
import harborSsExpenseReceiptPng from '@/assets/harbor_ss_expense_receipt.png';
import harborSsExportsPng from '@/assets/harbor_ss_exports.png';
import harborSsFileVaultPng from '@/assets/harbor_ss_file_vault.png';

// Declare global gtagSendEvent and fbq functions from index.html
declare global {
  interface Window {
    gtagSendEvent: (eventName: string) => void;
    fbq: (...args: unknown[]) => void;
  }
}

// Analytics handlers with Google Ads conversion tracking
const handleAppStoreClick = (location: string) => {
  track('Click App Store', { location });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_app_store');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'ClickAppStore', { location });
  }
};

const handleGooglePlayClick = (location: string) => {
  track('Click Google Play', { location });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_google_play');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'ClickGooglePlay', { location });
  }
};

const handleContactEmailClick = (location: string) => {
  track('Click Contact Email', { location });
};

const handleSupportSMSClick = (location: string) => {
  track('Click Support SMS', { location });
};

const handleFeatureTabClick = (tab: string) => {
  track('Click Feature Tab', { tab, audience: 'practitioners' });
};

const handleCopyRecommendation = (text: string) => {
  track('Copy Client Link', { location: 'Learn Practitioners Pricing' });
  navigator.clipboard.writeText(text).catch((err) => {
    console.error('Failed to copy text:', err);
  });
};

// Format phone number for display (e.g., +14157469059 -> (415) 746-9059)
const formatPhoneNumber = (phone: string): string => {
  // Remove +1 and any non-digits
  const digits = phone.replace(/\D/g, '').replace(/^1/, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Fallback to original if format is unexpected
  return phone;
};

// Support info response type
interface SupportInfoResponse {
  phone_number: string;
}

// Feature data for practitioners
const features = [
  {
    id: 'expenses',
    icon: FiDollarSign,
    title: 'Expense Tracking',
    shortTitle: 'Expenses',
    description:
      'Help clients track and document all shared expenses. Every expense is timestamped, categorized, and stored with receipt attachments for complete transparency.',
    details: [
      'Complete audit trail of all child-related expenses',
      'Receipt attachments for documentation',
      'Automatic split calculations',
      'Running balance between co-parents',
    ],
    image: harborSs1Png,
  },
  {
    id: 'calendar',
    icon: FiCalendar,
    title: 'Custody Calendar',
    shortTitle: 'Calendar',
    description:
      'Give clients a clear view of custody schedules for legal proceedings. Document parenting time with precision and track schedule modifications.',
    details: [
      'Visual custody schedule documentation',
      'Exception tracking for schedule changes',
      'Historical record of parenting time',
      'Supports any custody arrangement',
    ],
    image: harborSsCalendarPng,
  },
  {
    id: 'messaging',
    icon: FiMessageCircle,
    title: 'Secure Messaging',
    shortTitle: 'Messages',
    description:
      'Provide clients with tamper-proof communication records. All messages are timestamped and cannot be edited or deleted, ensuring reliable documentation.',
    details: [
      'Immutable message history',
      'Timestamped communications',
      'File and image sharing with records',
      'No "he said, she said" disputes',
    ],
    image: harborSsChatPng,
  },
  {
    id: 'records',
    icon: FiFileText,
    title: 'Court-Ready Records',
    shortTitle: 'Records',
    description:
      'Export comprehensive reports for court filings. Clients can email you detailed expense, payment, and communication records at any time.',
    details: [
      'PDF and CSV export formats',
      'Complete financial history',
      'Message records with full context',
      'Professionally formatted for legal use',
    ],
    image: harborSsExpenseReceiptPng,
  },
  {
    id: 'exports',
    icon: FiDownload,
    title: 'One-Tap Exports',
    shortTitle: 'Exports',
    description:
      'Clients can export all their records in seconds. With one tap, they select what they need — messages, expenses, or calendar — and send comprehensive reports directly to you.',
    details: [
      'Export with just one tap',
      'Choose specific data: chat, expenses, or calendar',
      'Email reports directly to attorneys or mediators',
      'Court-ready documentation in seconds',
    ],
    image: harborSsExportsPng,
  },
  {
    id: 'multiplatform',
    icon: FiSmartphone,
    title: 'iOS, Android & Web',
    shortTitle: 'Apps',
    description:
      'Clients can use Harbor from their phone or desktop. The platform works seamlessly across all devices for maximum accessibility.',
    details: [
      'Native mobile apps for iOS and Android',
      'Full-featured web application',
      'Data synced across all devices',
      'Accessible anywhere, anytime',
    ],
    image: laptopExamplePng,
  },
  {
    id: 'notifications',
    icon: FiBell,
    title: 'Push Notifications',
    shortTitle: 'Alerts',
    description:
      'Clients stay informed in real-time. Instant notifications ensure both co-parents are always up-to-date on expenses, messages, and schedule changes.',
    details: [
      'Real-time updates for all activity',
      'Customizable notification preferences',
      'Ensures timely responses',
      'Reduces communication delays',
    ],
    image: devicesPng,
  },
  {
    id: 'vault',
    icon: FiFolder,
    title: 'Document Vault',
    shortTitle: 'Vault',
    description:
      'Help clients organize important documents and photos for each child. Both parents can securely store and access files, keeping everything in one place for easy reference.',
    details: [
      'Dedicated vault for each child',
      'Store photos, documents, and files',
      'Both parents can view and upload',
      'Organized storage for legal documentation',
    ],
    image: harborSsFileVaultPng,
  },
];

// Header Component
function LearnHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <div className='flex items-center'>
              <img src={shipSvg} alt='' className='w-5 h-5 mb-1 mr-2' />
              <span className='flex items-center'>Harbor</span>
            </div>
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

// Feature Tab Carousel
function FeatureTabCarousel({
  selectedFeature,
  onSelectFeature,
}: {
  selectedFeature: string;
  onSelectFeature: (id: string) => void;
}) {
  return (
    <div className='w-full overflow-x-auto scrollbar-hide'>
      <div className='flex gap-2 md:gap-4 min-w-max md:min-w-0 md:justify-center px-4 md:px-0'>
        {features.map((feature) => {
          const Icon = feature.icon;
          const isSelected = selectedFeature === feature.id;
          return (
            <button
              key={feature.id}
              onClick={() => {
                handleFeatureTabClick(feature.shortTitle);
                onSelectFeature(feature.id);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all whitespace-nowrap ${
                isSelected
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: isSelected ? 600 : 400,
              }}
            >
              <Icon className='w-4 h-4' />
              {feature.shortTitle}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Feature Content Display
function FeatureContent({ featureId }: { featureId: string }) {
  const feature = features.find((f) => f.id === featureId) ?? features[0];
  const Icon = feature.icon;

  return (
    <div className='grid lg:grid-cols-2 gap-12 items-center'>
      {/* Image */}
      <div className='flex justify-center'>
        <img
          src={feature.image}
          alt={feature.title}
          className='w-full max-w-sm h-auto object-contain'
          style={{ maxHeight: '400px' }}
        />
      </div>

      {/* Content */}
      <div>
        <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6'>
          <Icon className='w-7 h-7 text-primary' />
        </div>
        <h3
          className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 700,
          }}
        >
          {feature.title}
        </h3>
        <p
          className='text-lg text-gray-600 mb-6 leading-relaxed'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 400,
          }}
        >
          {feature.description}
        </p>
        <ul className='space-y-3'>
          {feature.details.map((detail) => (
            <li key={detail} className='flex items-start gap-3'>
              <FiCheckCircle className='w-5 h-5 text-primary flex-shrink-0 mt-0.5' />
              <span
                className='text-gray-600'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                }}
              >
                {detail}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Pricing Section for Practitioners
function PricingSection() {
  const recommendationText =
    'Harbor is an affordable ($9.99/month) co-parenting app with expense tracking, custody calendar support, and timestamped chat, available at https://harborcoparent.com';

  return (
    <section className='bg-white py-24'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-12'>
          <span
            className='text-primary font-semibold text-lg mb-2 block'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 600,
            }}
          >
            Recommend to Clients
          </span>
          <h2
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Affordable for Your Clients
          </h2>
          <p
            className='text-xl text-gray-600'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            A fraction of the cost of other co-parenting apps.
          </p>
        </div>

        {/* Affordability Comparison */}
        <div className='mb-8 flex justify-center'>
          <div className='bg-emerald-50/50 border border-emerald-200/50 rounded-lg px-4 py-2.5 flex items-center gap-3 max-w-xl'>
            <FiTrendingDown className='w-4 h-4 text-emerald-600 flex-shrink-0' />
            <p
              className='text-sm text-emerald-700'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <span className='font-bold'>60% less</span> than other
              co-parenting apps — your clients save{' '}
              <span className='font-bold'>$180+ per year</span>
            </p>
          </div>
        </div>

        <div className='grid md:grid-cols-2 gap-8'>
          {/* Pricing Card */}
          <div className='border-2 border-primary bg-primary/5 rounded-2xl p-8'>
            <div className='text-center mb-6'>
              <h3
                className='text-2xl font-bold text-gray-900 mb-4'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 700,
                }}
              >
                Harbor Pro
              </h3>
              <div
                className='text-4xl font-bold text-gray-900 mb-1'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 700,
                }}
              >
                $9.99
                <span
                  className='text-lg font-normal text-gray-500'
                  style={{ fontWeight: 400 }}
                >
                  {' '}
                  / month
                </span>
              </div>
              <div
                className='text-xl text-gray-600'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                }}
              >
                or $49.99 / year
              </div>
            </div>

            <ul className='space-y-3 mb-8'>
              {getPricingFeatures('learn').map((feature: PricingFeature) => {
                const Icon = feature.icon;
                return (
                  <li key={feature.id} className='flex items-center gap-3'>
                    <Icon className='w-4 h-4 text-primary flex-shrink-0' />
                    <span
                      className='text-gray-600'
                      style={{
                        fontFamily:
                          "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      {feature.text}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Copy Recommendation Button */}
            <button
              onClick={() => handleCopyRecommendation(recommendationText)}
              className='flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <FiCopy className='w-5 h-5' />
              <span>Copy Client Link</span>
            </button>
          </div>

          {/* Free Exports Callout */}
          <div className='bg-gray-50 rounded-2xl p-8 border border-gray-200'>
            <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6'>
              <FiDownload className='w-7 h-7 text-primary' />
            </div>
            <h3
              className='text-2xl font-bold text-gray-900 mb-4'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Free Export Reports
            </h3>
            <p
              className='text-gray-600 mb-6 leading-relaxed'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Your clients can email you comprehensive export reports with all
              records at any time — completely free. No additional fees for
              documentation you need for legal proceedings.
            </p>
            <ul className='space-y-3'>
              {[
                'Unlimited PDF & CSV exports',
                'Email reports directly to you',
                'Complete expense & payment history',
                'Full message records',
                'No extra cost for exports',
              ].map((item) => (
                <li key={item} className='flex items-center gap-3'>
                  <FiCheckCircle className='text-primary flex-shrink-0' />
                  <span
                    className='text-gray-600'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  // Fetch support info
  const supportQuery = useQuery({
    queryKey: ['support-info'],
    queryFn: () => fetchApi<SupportInfoResponse>('/v1/support-info'),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const supportPhone = supportQuery.data?.phone_number ?? '+14157469059';

  return (
    <section className='bg-primary py-20'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-12'>
          <h2
            className='text-3xl md:text-4xl font-bold text-white mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Partner With Us
          </h2>
          <p
            className='text-xl text-white/80'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            Questions about recommending Harbor to your clients? We're here to
            help.
          </p>
        </div>

        <div className='grid md:grid-cols-2 gap-6'>
          {/* Email Contact */}
          <a
            href='mailto:team@harborcoparent.com'
            onClick={() => handleContactEmailClick('Learn Practitioners')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors group'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors'>
              <FiMail className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Email Us
            </h3>
            <p
              className='text-white/80 mb-3'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Reach out about partnership opportunities or client questions.
            </p>
            <span className='text-white font-medium'>
              team@harborcoparent.com →
            </span>
          </a>

          {/* SMS Support */}
          <a
            href={`sms:${supportPhone}`}
            onClick={() => handleSupportSMSClick('Learn Practitioners')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors group'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors'>
              <FiMessageSquare className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Text Support
            </h3>
            <p
              className='text-white/80 mb-3'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Support available 24/7 — text us anytime.
            </p>
            <span className='text-white font-medium'>
              Text {formatPhoneNumber(supportPhone)} →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

// App Store Buttons Section
function AppStoreButtonsSection() {
  return (
    <section className='bg-white py-12'>
      <div className='max-w-4xl mx-auto px-6'>
        <h2
          className='text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 700,
          }}
        >
          Download At
        </h2>
        <div className='flex flex-col md:flex-row items-center justify-center gap-4'>
          <a
            href={getAppStoreUrl('learn-practitioners-bottom', getUtmParamsFromUrl())}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => handleAppStoreClick('Learn Practitioners Bottom')}
            className='flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 600,
            }}
          >
            <FaApple size={20} />
            <span>App Store</span>
          </a>
          <a
            href={getPlayStoreUrl('learn-practitioners-bottom', getUtmParamsFromUrl())}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => handleGooglePlayClick('Learn Practitioners Bottom')}
            className='flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 600,
            }}
          >
            <FaGooglePlay size={20} />
            <span>Google Play</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Main Page Component
function LearnPractitionersPage() {
  const [selectedFeature, setSelectedFeature] = useState('expenses');

  useEffect(() => {
    track('Learn Practitioners Screen');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor for Practitioners | Tools for Divorce Coaches & Family Professionals'
        description='Invite clients to Harbor, monitor their co-parenting progress, and provide better support with professional tools designed for divorce coaches and family law practitioners.'
        path='/learn/practitioners'
      />
      <LearnHeader />

      {/* Hero Section */}
      <section className='bg-primary/5 pt-32 pb-8'>
        <div className='max-w-4xl mx-auto px-6 text-center'>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Harbor for Practitioners
          </h1>
          <p
            className='text-xl text-gray-600 max-w-2xl mx-auto'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            A powerful tool for family lawyers, divorce coaches, and mediators.
            Help your clients document everything they need for successful
            co-parenting.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className='py-16 bg-sky-light'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='text-center mb-12'>
            <span
              className='text-primary font-semibold text-lg mb-2 block'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              Features
            </span>
            <h2
              className='text-3xl md:text-4xl font-bold text-gray-900 mb-8'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Tools Your Clients Need
            </h2>
            <FeatureTabCarousel
              selectedFeature={selectedFeature}
              onSelectFeature={setSelectedFeature}
            />
          </div>

          <div className='mt-12'>
            <FeatureContent featureId={selectedFeature} />
          </div>
        </div>
      </section>

      <PricingSection />
      <AppStoreButtonsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/learn/practitioners')({
  component: LearnPractitionersPage,
});
